import { useEffect, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { RouteErrorBoundary } from '@/components/ui/ErrorBoundary';
import { Sidebar, type NavSection } from '@/components/layout/Sidebar';
import { Topbar } from '@/components/layout/Topbar';
import { ADMIN_NAV } from './navConfig';
import { titleForPath } from './pages/titleForPath';
import { ToastHost } from '@/components/ui/Toast';
import { FloatingSoftphone } from '@/personas/agent/components/FloatingSoftphone';
import { WrapupOverlay } from '@/personas/agent/components/WrapupOverlay';
import { CustomerCard } from '@/personas/agent/components/CustomerCard';
import { useCustomerCardPop } from '@/personas/agent/hooks/useCustomerCardPop';
import { PersistentAudioBar } from '@/components/layout/PersistentAudioBar';
import { SnoopControlBar } from '@/components/layout/SnoopControlBar';
import { IncomingCallModal } from '@/personas/agent/components/IncomingCallModal';
import { MicBlockedModal } from '@/personas/agent/components/MicBlockedModal';
import { useSipRegistration } from '@/personas/agent/stores/useSipRegistration';
import { usePresenceLiveness } from '@/shared/hooks/usePresenceLiveness';
import { useAuthStore } from '@/shared/stores/auth';
import { canView } from '@/shared/lib/permissions';
import { me } from '@/shared/api/authApi';
import { useCardGlare } from '@/shared/hooks/useCardGlare';

export function AdminLayout() {
  // Managers/supervisors get the same softphone as agents when they have an
  // extension; users without one register in "no line" mode (toggle hidden).
  useSipRegistration('admin');
  // גם מנהל מופיע בניטור החי — "מחובר" אמיתי דורש פעימה (01/10/2026).
  usePresenceLiveness({ ping: true });
  useCardGlare();
  // כרטיס הלקוח נפתח אוטומטית בשיחה (הסופטפון הצף זמין גם כאן).
  useCustomerCardPop();

  const user = useAuthStore((s) => s.user);
  const tenant = useAuthStore((s) => s.tenant);
  const setTenant = useAuthStore((s) => s.setTenant);

  // ה-login לא מחזיר את המרכזייה — שם המרכזייה (לשבב בסרגל הצד) נשלף
  // פעם אחת מ-/admin/me ונשמר ב-store (persist).
  useEffect(() => {
    if (tenant || !user || user.is_super_admin) return;
    let alive = true;
    me('admin').then((r) => { if (alive && r.tenant) setTenant(r.tenant); }).catch(() => {});
    return () => { alive = false; };
  }, [tenant, user, setTenant]);
  const { pathname } = useLocation();
  const pageTitle = titleForPath(pathname);

  // סינון התפריט לפי ההרשאות האפקטיביות של המשתמש (RBAC דינמי).
  const nav = useMemo<NavSection[]>(
    () => ADMIN_NAV
      .map((section) => ({ ...section, items: section.items.filter((it) => canView(user, it.perm)) }))
      .filter((section) => section.items.length > 0),
    [user],
  );

  return (
    <div style={{ position: 'relative', display: 'flex', height: '100vh', overflow: 'hidden', background: 'var(--bg)', isolation: 'isolate' }}>
      <a href="#main-content" className="skip-link">דלג לתוכן</a>
      {/* רקע "בועות" רכות של עיצוב v2 — דקורטיבי בלבד. */}
      <div className="app-blobs" aria-hidden="true"><span /><span /><span /></div>
      <Sidebar brand="" sections={nav} />
      <div style={{ position: 'relative', zIndex: 1, flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Topbar title={pageTitle} />
        <main id="main-content" aria-label="תוכן ראשי" style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', minHeight: 0, padding: '4px 6px 12px' }}>
          <RouteErrorBoundary>
            {/* מעבר מסך: התוכן "עולה" בכל ניווט (gcUp, כמו בעיצוב). */}
            <div key={pathname} className="page-enter" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <Outlet />
            </div>
          </RouteErrorBoundary>
        </main>
      </div>
      <IncomingCallModal />
      <MicBlockedModal />
      {/* חייב להתלוות ל-FloatingSoftphone: הוא היחיד שקורא ל-finishWrapup,
          ובלעדיו הסופטפון נתקע ב-wrap-up אחרי שיחה ואי אפשר לחייג שוב. */}
      <WrapupOverlay />
      <CustomerCard />
      <FloatingSoftphone />
      <PersistentAudioBar />
      <SnoopControlBar />
      <ToastHost />
    </div>
  );
}
